import type { UseQueryOptions } from "@tanstack/react-query";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";

import {
  fetchTestCaseStats,
  fetchManualTestCase,
  fetchAutomatedTestCase,
  fetchStepGroups,
  fetchPerformanceTestCase,
  fetchAllTestCases,
  fetchTestCaseRunHistory,
  fetchTestCaseCycleRuns,
  fetchTestCaseRequirements,
  fetchTestCaseTypes,
  fetchTestCasePriorities,
  fetchPriorities,
  fetchTestData,
  fetchRequirements,
  fetchTeamMembers,
  fetchDevelopers,
  fetchTestTags,
  fetchTestCaseTagsById,
  fetchDefectTagsById,
  fetchReadyTestCases,
  validateURLAccess,
  fetchTestCaseById,
  fetchTestCaseActivity,
  fetchTestCaseComments,
  fetchExtensionStatus,
  fetchNaturalTextActions,
  fetchTestStepDetails,
  fetchTestSteps,
  fetchForLoopConditions,
  fetchAllTestData,
  fetchDryRunHistory,
  fetchPlatformScreenResolution,
  fetchPlatformOsVersion,
  fetchPlatformBrowsers,
  fetchCloudDevice,
  fetchAgentDevices,
  fetchDefectLogs,
  fetchAllElementList,
  fetchElementScreenName,
  fetchElementById,
  fetchNaturalTextActionExamples,
  fetchSubFolderList,
  fetchFolderList,
  fetchFolderDetails,
  fetchFolderStats,
  fetchElementByName,
  fetchElementLinkedEntities,
  fetchTestCaseLinkedEntities,
  fetchTestCaseTypeById,
  fetchTestCasePriorityById,
  fetchTestCaseStatuses,
  fetchTestCaseStatusById,
  fetchTestDataById,
  fetchComments,
  fetchDefectComments,
  fetchHistory,
  fetchAllDefectLogs,
  fetchDataGenerators,
  fetchStoreActionNlpTemplates,
  fetchStepsForNlpTemplateIds,
  fetchManualTestRuns,
  fetchManualTestRun,
  fetchTestPlansForTestCases,
} from "./scenarios.api";

interface QueryParams {
  versionId: number;
  testCaseFolderId?: number;
  page?: number;
  size?: number;
  query?: string;
  sort?: string;
  deleted?: boolean;
  tags?: string;
}

type GenericQueryParams = {
  query?: string;
  sort?: string;
  page?: number;
  size?: number;
  [key: string]: string | number | boolean | null | undefined;
};

interface TestStepMeta {
  id: string;
  position: string;
}

// Manual
export const useManualTestCases = (params: Omit<QueryParams, "page">) =>
  useInfiniteQuery({
    queryKey: ["manualTestCases", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchManualTestCase({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!params.versionId,
  });

// Automated
export const useAutomatedTestCases = (params: Omit<QueryParams, "page">) =>
  useInfiniteQuery({
    queryKey: ["automatedTestCases", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchAutomatedTestCase({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!params.versionId,
  });

// Step Groups
export const useStepGroups = (
  params: Omit<QueryParams, "page" | "testCaseFolderId">,
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: ["stepGroups", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchStepGroups({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: enabled && !!params.versionId,
  });

// Performance
export const usePerformanceTestCases = (params: Omit<QueryParams, "page">) =>
  useInfiniteQuery({
    queryKey: ["performanceTestCases", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchPerformanceTestCase({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!params.testCaseFolderId,
  });
export const useAllTestCases = (params: QueryParams) =>
  useQuery({
    queryKey: ["allTestCases", params],
    queryFn: () => fetchAllTestCases(params),
    enabled: !!params.versionId,
  });

// export const useAllFolderTestCases = (params: QueryParams) =>
//   useQuery({
//     queryKey: ["allTestCases", params],
//     queryFn: () => fetchAllTestCases(params),
//     enabled: !!params.testCaseFolderId,
//   });

export const useAllFolderTestCases = (
  params: Omit<QueryParams, "page">,
  enabled = true,
) =>
  useInfiniteQuery({
    // Namespaced separately from "allTestCases" (the plain useQuery key) so the
    // infinite/paged cache shape can't collide with the flat list cache shape.
    queryKey: ["allFolderTestCases", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchAllTestCases({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: enabled && !!params.testCaseFolderId,
    staleTime: 30_000, // 30s — honour prefetch cache from hover
  });

/**
 * Fetch ALL test cases for a workspace version in one paginated query, so the
 * folder tree can partition them client-side by folderId instead of issuing a
 * separate request per expanded folder (the per-folder N+1). Auto-paginated by
 * the consumer (FolderCasesProvider) via fetchNextPage until all pages load.
 */
export const useAllVersionTestCases = (
  params: {
    versionId?: string | number;
    scriptType?: string;
    isStepGroup?: boolean;
    includeStepGroups?: boolean;
    deleted?: boolean;
  },
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: ["versionTestCasesAll", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchAllTestCases({
        versionId: params.versionId as any,
        page: pageParam as number,
        size: 200,
        isStepGroup: params.isStepGroup,
        includeStepGroups: params.includeStepGroups,
        deleted: params.deleted,
      } as any),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: enabled && !!params.versionId,
    staleTime: 30_000,
  });

export const useAllDryRunHistory = (
  params: Omit<GenericQueryParams, "page">,
  testCaseId: string,
) =>
  useInfiniteQuery({
    queryKey: ["dryRunHistory", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchDryRunHistory({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!testCaseId,
    staleTime: 10 * 1000,
    refetchOnMount: "always",
  });

export const useTestCaseRunHistory = (testCaseId: number | string | null) =>
  useQuery({
    queryKey: ["testCaseRunHistory", testCaseId],
    queryFn: () => fetchTestCaseRunHistory(testCaseId!),
    enabled: !!testCaseId,
    staleTime: 30 * 1000,
  });

export const useTestCaseCycleRuns = (
  testCaseId: number | string | null,
  variant: "automation" | "manual" = "automation",
  enabled = true,
) =>
  useQuery({
    queryKey: ["testCaseCycleRuns", String(testCaseId), variant],
    queryFn: () =>
      fetchTestCaseCycleRuns(testCaseId!, { size: 50, type: variant }),
    enabled: !!testCaseId && enabled,
    staleTime: 30 * 1000,
    refetchOnMount: "always",
  });

export const useTestCaseRequirements = (testCaseId: number | string | null) =>
  useQuery({
    queryKey: ["testCaseRequirements", testCaseId],
    queryFn: () => fetchTestCaseRequirements(testCaseId!),
    enabled: !!testCaseId,
    staleTime: 30 * 1000,
  });

export const useTestCaseTypes = (projectId: number, enabled?: boolean) =>
  useQuery({
    queryKey: ["testCaseTypes", projectId],
    queryFn: () => fetchTestCaseTypes(projectId),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useTestCaseTypeById = (testCaseTypeId: number) =>
  useQuery({
    queryKey: ["testCaseTypeId", testCaseTypeId],
    queryFn: () => fetchTestCaseTypeById(testCaseTypeId),
    enabled: !!testCaseTypeId,
    staleTime: 5 * 60 * 1000,
  });

export const useTestCasePriorities = (projectId: number, enabled?: boolean) =>
  useQuery({
    queryKey: ["testCasePriorities", projectId],
    queryFn: () => fetchTestCasePriorities(projectId),
    enabled,
    // Workspace-level lookup keyed by projectId — cache it so switching test
    // cases doesn't refetch. Mutations invalidate this key when priorities change.
    staleTime: 5 * 60 * 1000,
  });

// Global priorities (Critical/High/Medium/Low) — not project-scoped.
const STANDARD_PRIORITIES = new Set(["critical", "high", "medium", "low"]);
export const usePriorities = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["priorities"],
    queryFn: () => fetchPriorities(),
    enabled,
    staleTime: 5 * 60 * 1000,
    select: (res: any) => {
      const items =
        res?.data?.content ?? res?.data ?? res?.content ?? res ?? [];
      const filtered = Array.isArray(items)
        ? items.filter(
            (p: any) =>
              !p.isDeleted &&
              STANDARD_PRIORITIES.has(
                (p.displayName ?? p.name ?? "").toLowerCase(),
              ),
          )
        : items;
      // Preserve original response shape if it was wrapped
      if (res?.data?.content)
        return { ...res, data: { ...res.data, content: filtered } };
      if (res?.data && Array.isArray(res.data))
        return { ...res, data: filtered };
      if (res?.content) return { ...res, content: filtered };
      return filtered;
    },
  });

export const useTestCasePriorityById = (priorityId: number) =>
  useQuery({
    queryKey: ["testCasePriorityById", priorityId],
    queryFn: () => fetchTestCasePriorityById(priorityId),
    enabled: !!priorityId,
    staleTime: 5 * 60 * 1000,
  });

export const useTestCaseStatuses = (projectId: number, enabled?: boolean) =>
  useQuery({
    queryKey: ["testCaseStatuses", projectId],
    queryFn: () => fetchTestCaseStatuses(projectId),
    enabled,
    // Workspace-level lookup keyed by projectId — cache it so switching test
    // cases doesn't refetch. Mutations invalidate this key when statuses change.
    staleTime: 5 * 60 * 1000,
  });

export const useTestCaseStatusById = (statusId: number) =>
  useQuery({
    queryKey: ["testCaseStatusById", statusId],
    queryFn: () => fetchTestCaseStatusById(statusId),
    enabled: !!statusId,
    staleTime: 5 * 60 * 1000,
  });

export const useTestDataById = (testDataId: number) =>
  useQuery({
    queryKey: ["testDataById", testDataId],
    queryFn: () => fetchTestDataById(testDataId),
    enabled: !!testDataId,
    staleTime: 5 * 60 * 1000,
  });

export const useTestDataQueries = (testDataIds: number[] = []) =>
  useQueries({
    queries: testDataIds.map((id) => ({
      queryKey: ["testData", id],
      queryFn: async () => {
        const response = await fetchTestData(id);
        return response.data?.content?.[0] ?? null;
      },
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })),
  });

export const useRequirements = (versionId: number, enabled?: boolean) =>
  useQuery({
    queryKey: ["requirements", versionId],
    queryFn: () => fetchRequirements(versionId),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useTeamMembers = (enabled?: boolean) =>
  useQuery({
    queryKey: ["teamMembers"],
    queryFn: () => fetchTeamMembers(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 min - rarely changes
  });

/** Returns only Developer / Automation Engineer users in the current project. */
export const useDevelopers = (enabled?: boolean) =>
  useQuery({
    queryKey: ["developers"],
    queryFn: () => fetchDevelopers(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useReadyTestCases = (versionId: number, enabled?: boolean) =>
  useQuery({
    queryKey: ["readyTestCases", versionId],
    queryFn: () => fetchReadyTestCases(versionId),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useTestTags = (enabled?: boolean) => {
  return useQuery({
    queryKey: ["testTags"],
    queryFn: fetchTestTags,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useTestCaseTagsById = (
  testCaseId: string | number,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ["testCaseTagsById", String(testCaseId)],
    queryFn: () => fetchTestCaseTagsById(testCaseId),
    enabled: enabled !== false && !!testCaseId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useDefectTagsById = (
  defectId: string | number,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: ["defectTagsById", String(defectId)],
    queryFn: () => fetchDefectTagsById(defectId),
    enabled: enabled !== false && !!defectId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useValidateURL = (url: string, enabled: boolean) =>
  useQuery({
    queryKey: ["validateURL", url],
    queryFn: () => validateURLAccess(url),
    enabled,
  });

// Test Page

export const useTestCaseDetailsQuery = (
  testCaseId: string | number,
  enabled?: boolean,
) =>
  useQuery({
    queryKey: ["testCaseDetails", String(testCaseId)],
    queryFn: () => fetchTestCaseById(testCaseId),
    enabled: enabled !== false && !!testCaseId,
    // The edit dialog unmounts on close, so it has no live observer to receive
    // a post-save invalidation. Force a fresh network fetch every time the
    // dialog (or detail panel) mounts so reopening a just-edited case always
    // shows the saved values instead of stale cached data.
    refetchOnMount: "always",
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const useTestCaseActivityQuery = (
  versionId: string,
  testCaseId: string,
  externalEnabled = true,
) =>
  useQuery({
    queryKey: ["testCaseActivity", versionId, testCaseId],
    queryFn: () => fetchTestCaseActivity(versionId, testCaseId),
    enabled: externalEnabled && !!versionId && !!testCaseId,
    staleTime: 5 * 60 * 1000,
  });

export const useTestCaseCommentsQuery = (
  versionId: string | number,
  testCaseId: string | number,
  externalEnabled = true,
) =>
  useQuery({
    queryKey: ["testCaseComments", versionId, testCaseId],
    queryFn: () => fetchTestCaseComments(versionId, testCaseId),
    enabled: externalEnabled && !!versionId && !!testCaseId,
    staleTime: 2 * 60 * 1000,
  });

export const useExtensionStatusQuery = (enabled = true) =>
  useQuery({
    queryKey: ["extensionStatus"],
    queryFn: () => fetchExtensionStatus(),
    enabled,
    staleTime: 10 * 60 * 1000,
  });

export const useNaturalTextActionsQuery = (params?: {
  search?: string;
  page?: number;
  size?: number;
  workspaceType?: string;
  applicationType?: string;
}) =>
  useQuery({
    queryKey: ["naturalTextActions", params],
    queryFn: () => fetchNaturalTextActions(params),
    staleTime: 5 * 60 * 1000,
  });

export const useNaturalTextActionExampleQuery = (naturalTextActionId: string) =>
  useQuery({
    queryKey: ["naturalTextActionsExamples", naturalTextActionId],
    queryFn: () => fetchNaturalTextActionExamples(naturalTextActionId),
    enabled: !!naturalTextActionId,
  });

export const useTestStepDetailsQueries = (
  testCaseId: string,
  testStepsMeta: TestStepMeta[],
) => {
  return useQueries({
    queries: testStepsMeta.map(({ id, position }) => ({
      queryKey: ["testStepDetails", testCaseId, position, id],
      queryFn: () => fetchTestStepDetails(testCaseId, position, id),
      enabled: !!testCaseId && !!id,
    })),
  });
};

export const useTestStepDetailsById = (
  testCaseId: string,
  testStepId: string,
  position: string,
) =>
  useQuery({
    queryKey: ["testStepDetailsById", testStepId],
    queryFn: () => fetchTestStepDetails(testCaseId, position, testStepId),
    enabled: !!testStepId,
  });

export const useTestStepsQuery = (testCaseId: string | number) =>
  useQuery({
    queryKey: ["testSteps", String(testCaseId)],
    queryFn: () => fetchTestSteps(testCaseId),
    enabled: !!testCaseId,
    staleTime: 2 * 60 * 1000, // 2 min - test steps can be edited so don't cache too long
    gcTime: 10 * 60 * 1000,
  });

export const useForLoopConditionsQuery = (enabled = true) =>
  useQuery({
    queryKey: ["forLoopConditions"],
    queryFn: fetchForLoopConditions,
    enabled,
    staleTime: Infinity, // for-loop conditions are effectively static per-session
    gcTime: 24 * 60 * 60 * 1000,
  });

export const useAllTestDataQuery = (versionId: number, enabled = true) =>
  useQuery({
    queryKey: ["allTestData", versionId],
    queryFn: () => fetchAllTestData(versionId),
    enabled: enabled && !!versionId,
    staleTime: 5 * 60 * 1000,
  });

// Hook to fetch Platform Screen Resolution by ID and lab type
export const usePlatformScreenResolutionQuery = (
  platformScreenResolutionId: string,
  testPlanLabType: string,
) => {
  return useQuery({
    queryKey: [
      "platformScreenResolution",
      platformScreenResolutionId,
      testPlanLabType,
    ],
    queryFn: () =>
      fetchPlatformScreenResolution(
        platformScreenResolutionId,
        testPlanLabType,
      ),
    enabled: !!platformScreenResolutionId && !!testPlanLabType,
  });
};

// Hook to fetch Platform OS Version by ID and lab type
export const usePlatformOsVersionQuery = (
  platformOsVersionId: string,
  testPlanLabType: string,
) => {
  return useQuery({
    queryKey: ["platformOsVersion", platformOsVersionId, testPlanLabType],
    queryFn: () => fetchPlatformOsVersion(platformOsVersionId, testPlanLabType),
    enabled: !!platformOsVersionId && !!testPlanLabType,
  });
};

// Hook to fetch Browsers for platform, osVersion, workspaceType, labType
export const usePlatformBrowsersQuery = (
  platformId: string,
  osVersion: string | undefined,
  workspaceType: string,
  testPlanLabType: string,
) => {
  return useQuery({
    queryKey: [
      "platformBrowsers",
      platformId,
      osVersion,
      workspaceType,
      testPlanLabType,
    ],
    queryFn: () =>
      fetchPlatformBrowsers(
        platformId,
        osVersion,
        workspaceType,
        testPlanLabType,
      ),
    enabled:
      !!platformId && !!osVersion && !!workspaceType && !!testPlanLabType,
  });
};

// Hook to fetch Cloud Device info by platformDeviceId and labType
export const useCloudDeviceQuery = (
  platformDeviceId: string,
  testPlanLabType: string,
) => {
  return useQuery({
    queryKey: ["cloudDevice", platformDeviceId, testPlanLabType],
    queryFn: () => fetchCloudDevice(platformDeviceId, testPlanLabType),
    enabled: !!platformDeviceId && !!testPlanLabType,
  });
};

// Hook to fetch paginated agent devices filtered by provisioning status
export const useAgentDevicesQuery = (
  agentId: string,
  pageNumber: number,
  provisioning: boolean | string,
) => {
  return useQuery({
    queryKey: ["agentDevices", agentId, pageNumber, provisioning],
    queryFn: () => fetchAgentDevices(agentId, pageNumber, provisioning),
    enabled: !!agentId && pageNumber >= 0 && provisioning !== undefined,
  });
};

export const useFetchDefectLogs = (testCaseId: string, versionId: number) =>
  useInfiniteQuery({
    queryKey: ["defectLogs", testCaseId, versionId],
    queryFn: ({ pageParam = 0 }) =>
      fetchDefectLogs(testCaseId, pageParam as number, versionId),
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!testCaseId,
    staleTime: 30 * 1000,
    refetchOnMount: "always",
  });

export const useFetchAllDefectLogs = (testCaseId: number) =>
  useQuery({
    queryKey: ["defectLogs", testCaseId],
    queryFn: () => fetchAllDefectLogs(testCaseId),
    enabled: !!testCaseId,
  });

// Elements page queries
export const useAllElementList = (
  versionId: string,
  page: number,
  size?: number,
) => {
  return useQuery({
    queryKey: ["allElementList", versionId, page, size],
    queryFn: () => fetchAllElementList(versionId, page, size),
    enabled: !!versionId,
  });
};

export const useAllElementListInfinite = (
  versionId: string | number | undefined,
  size = 10,
  options?: { enabled?: boolean },
) =>
  useInfiniteQuery({
    queryKey: ["allElementList", versionId, size],
    queryFn: ({ pageParam = 0 }) =>
      fetchAllElementList(String(versionId), pageParam as number, size),
    initialPageParam: 0,
    getNextPageParam: (lastPage: any) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages =
        lastPage?.data?.totalPages ?? lastPage?.data?.total_pages ?? 1;
      if (lastPage?.data?.last === true) return undefined;
      if (lastPage?.data?.last === false) return currentPage + 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    enabled: !!versionId && (options?.enabled ?? true),
  });

export const useScreenName = (versionId: string) => {
  return useQuery({
    queryKey: ["allScreenName", versionId],
    queryFn: () => fetchElementScreenName(versionId),
    enabled: !!versionId,
  });
};

export const useElementDetails = (
  elementId: string,
  options?: Omit<
    UseQueryOptions<any, unknown, any, [string, string]>,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery({
    queryKey: ["fetchElementById", elementId],
    queryFn: () => fetchElementById(elementId),
    enabled: !!elementId,
    ...options,
  });
};

export const useElementDetailByName = (
  versionId: string,
  elementName: string,
) => {
  return useQuery({
    queryKey: ["fetchElementByName", versionId, elementName],
    queryFn: ({ queryKey }) => {
      const [, versionId, elementName] = queryKey;
      return fetchElementByName(versionId, elementName);
    },
    enabled: !!versionId && !!elementName,
  });
};

export const useElementLinkedEntities = (elementId: number | string | null) => {
  return useQuery({
    queryKey: ["elementLinkedEntities", elementId],
    queryFn: () => fetchElementLinkedEntities(elementId!),
    enabled: elementId != null,
  });
};

export const useTestCaseLinkedEntities = (
  testCaseId: number | string | null,
) => {
  return useQuery({
    queryKey: ["testCaseLinkedEntities", testCaseId],
    queryFn: () => fetchTestCaseLinkedEntities(testCaseId!),
    enabled: testCaseId != null,
  });
};

export const useTestCaseStats = (versionId?: number) => {
  return useQuery({
    queryKey: ["testCaseStats", versionId],
    queryFn: () => fetchTestCaseStats(versionId),
    enabled: !!versionId,
    staleTime: 30_000,
  });
};

export const useFetchFolderList = (
  versionId?: number,
  type?: string,
  countType?: string,
  options?: { refetchInterval?: number | false },
) => {
  return useQuery({
    queryKey: ["folderList", versionId, type, countType],
    queryFn: () => fetchFolderList(versionId, type, countType),
    staleTime: 5 * 60 * 1000,
    enabled: !!versionId,
    refetchInterval: options?.refetchInterval ?? false,
  });
};

export const useFetchSubFolderList = (parentFolderId: number) => {
  return useQuery({
    queryKey: ["subFolderList", parentFolderId],
    queryFn: () => fetchSubFolderList(parentFolderId),
    enabled: !!parentFolderId,
  });
};

export const useFetchFolderDetails = (folderId: number, isEdit: boolean) => {
  return useQuery({
    queryKey: ["folderDetails", folderId],
    queryFn: () => fetchFolderDetails(folderId),
    enabled: !!isEdit,
  });
};

/** Lazy-loaded folder stats for the hover tooltip. */
export const useFolderStats = (folderId: number | null) => {
  return useQuery({
    queryKey: ["folderStats", folderId],
    queryFn: () =>
      fetchFolderStats(folderId!).then((res) => (res as any)?.data ?? res),
    enabled: folderId != null,
    staleTime: 60_000,
  });
};

export const useGetComments = (
  workspaceVersionId: string,
  moduleId: number,
  moduleName: string,
) => {
  return useQuery({
    queryKey: ["comments", workspaceVersionId, moduleId, moduleName],
    queryFn: () =>
      fetchComments(workspaceVersionId, moduleId, moduleName).then(
        (res) => res.data,
      ),
    enabled: !!workspaceVersionId && !!moduleId && !!moduleName,
  });
};

/**
 * Fetches defect comments without workspace version scoping,
 * so all team members see the same comments regardless of their version preference.
 */
export const useGetDefectComments = (moduleId: number) => {
  return useQuery({
    queryKey: ["defectComments", moduleId],
    queryFn: () => fetchDefectComments(moduleId).then((res) => res.data),
    enabled: !!moduleId,
  });
};

export const useGetHistory = (
  workspaceVersionId: string,
  moduleId: number,
  moduleName: string,
) => {
  return useQuery({
    queryKey: ["history", workspaceVersionId, moduleId, moduleName],
    queryFn: () =>
      fetchHistory(workspaceVersionId, moduleId, moduleName).then(
        (res) => res.data,
      ),
    enabled: !!workspaceVersionId && !!moduleId && !!moduleName,
  });
};

// ── Data Generators ──────────────────────────────────────────────────────────

export const useDataGenerators = (search: string) =>
  useQuery({
    queryKey: ["dataGenerators", search],
    queryFn: () =>
      fetchDataGenerators(search).then((res: any) => res.data ?? res),
  });

// ── Runtime Variable store-steps ─────────────────────────────────────────────

export const useStoreActionNlpTemplates = (options?: any) =>
  useQuery({
    queryKey: ["storeActionNlpTemplates"],
    queryFn: () =>
      fetchStoreActionNlpTemplates().then((res: any) => res.data ?? res),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });

export const useStepsForNlpTemplateIds = (
  templateIds: number[],
  versionId: number,
  workspaceId: number,
  options?: any,
) =>
  useQuery({
    queryKey: ["stepsForNlpTemplates", templateIds, versionId, workspaceId],
    queryFn: () =>
      fetchStepsForNlpTemplateIds(templateIds, versionId, workspaceId).then(
        (res: any) => res.data ?? res,
      ),
    enabled:
      templateIds.length > 0 &&
      !!versionId &&
      !!workspaceId &&
      (options?.enabled ?? true),
  });

// ── Manual Test Runs ──────────────────────────────────────────────────────────

export const useManualTestRuns = (testCaseId: number | string | undefined) =>
  useQuery({
    queryKey: ["manualTestRuns", String(testCaseId)],
    queryFn: () =>
      fetchManualTestRuns(testCaseId!).then((res: any) => res.data ?? res),
    enabled: !!testCaseId,
  });

export const useManualTestRun = (runId: number | string | undefined) =>
  useQuery({
    queryKey: ["manualTestRun", String(runId)],
    queryFn: () =>
      fetchManualTestRun(runId!).then((res: any) => res.data ?? res),
    enabled: !!runId,
    staleTime: 0,
  });

// ── Test Plans for Test Cases (bulk reverse lookup) ───────────────────────────

export const useTestPlansForTestCases = (
  testCaseIds: (number | string)[],
  enabled = true,
) => {
  const stableKey = [...testCaseIds]
    .map((v) => Number(v))
    .sort((a, b) => a - b);
  return useQuery({
    queryKey: ["testPlansForTestCases", stableKey],
    queryFn: () =>
      fetchTestPlansForTestCases(stableKey).then((res: any) => res.data ?? res),
    enabled: enabled && stableKey.length > 0,
    staleTime: 30_000,
  });
};
